import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Divider,
  Breadcrumbs,
  Link
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import { useTranslation } from 'react-i18next';

// Mock data structure for folders and images
// In a real application, this would come from an API
interface FileItem {
  id: string;
  name: string;
  type: 'folder' | 'image';
  path: string;
  url?: string;
  children?: FileItem[];
}

// Sample data structure
const sampleData: FileItem[] = [
  {
    id: '1',
    name: 'Images',
    type: 'folder',
    path: 'Images',
    children: [
      {
        id: '1-1',
        name: 'Nature',
        type: 'folder',
        path: 'Images/Nature',
        children: [
          {
            id: '1-1-1',
            name: 'Mountains.jpg',
            type: 'image',
            path: 'Images/Nature/Mountains.jpg',
            url: 'https://via.placeholder.com/150/0000FF/808080?text=Mountains'
          },
          {
            id: '1-1-2',
            name: 'Beach.jpg',
            type: 'image',
            path: 'Images/Nature/Beach.jpg',
            url: 'https://via.placeholder.com/150/FF0000/FFFFFF?text=Beach'
          }
        ]
      },
      {
        id: '1-2',
        name: 'City',
        type: 'folder',
        path: 'Images/City',
        children: [
          {
            id: '1-2-1',
            name: 'NewYork.jpg',
            type: 'image',
            path: 'Images/City/NewYork.jpg',
            url: 'https://via.placeholder.com/150/00FF00/000000?text=NewYork'
          },
          {
            id: '1-2-2',
            name: 'Paris.jpg',
            type: 'image',
            path: 'Images/City/Paris.jpg',
            url: 'https://via.placeholder.com/150/FFFF00/000000?text=Paris'
          }
        ]
      },
      {
        id: '1-3',
        name: 'Sunset.jpg',
        type: 'image',
        path: 'Images/Sunset.jpg',
        url: 'https://via.placeholder.com/150/FFA500/000000?text=Sunset'
      }
    ]
  },
  {
    id: '2',
    name: 'Logos',
    type: 'folder',
    path: 'Logos',
    children: [
      {
        id: '2-1',
        name: 'Company.jpg',
        type: 'image',
        path: 'Logos/Company.jpg',
        url: 'https://via.placeholder.com/150/800080/FFFFFF?text=Company'
      }
    ]
  }
];

interface ImageUploaderProps {
  open: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string) => void;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ open, onClose, onSelectImage }) => {
  const { t } = useTranslation();
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [currentItems, setCurrentItems] = useState<FileItem[]>(sampleData);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentPath([]);
      setCurrentItems(sampleData);
      setSelectedImage(null);
      setPreviewImage(null);
    }
  }, [open]);

  // Navigate to a folder
  const handleFolderClick = (folder: FileItem) => {
    if (folder.children) {
      setCurrentPath([...currentPath, folder.name]);
      setCurrentItems(folder.children);
      setSelectedImage(null);
      setPreviewImage(null);
    }
  };

  // Navigate back to parent folder
  const handleBackClick = () => {
    if (currentPath.length > 0) {
      const newPath = [...currentPath];
      newPath.pop();
      
      // Find the items for the new path
      let items = sampleData;
      for (const folder of newPath) {
        const foundFolder = items.find(item => item.name === folder && item.type === 'folder');
        if (foundFolder && foundFolder.children) {
          items = foundFolder.children;
        }
      }
      
      setCurrentPath(newPath);
      setCurrentItems(items);
      setSelectedImage(null);
      setPreviewImage(null);
    }
  };

  // Navigate to root
  const handleHomeClick = () => {
    setCurrentPath([]);
    setCurrentItems(sampleData);
    setSelectedImage(null);
    setPreviewImage(null);
  };

  // Navigate to specific path in breadcrumb
  const handleBreadcrumbClick = (index: number) => {
    if (index < 0) {
      handleHomeClick();
      return;
    }

    const newPath = currentPath.slice(0, index + 1);
    
    // Find the items for the new path
    let items = sampleData;
    for (const folder of newPath) {
      const foundFolder = items.find(item => item.name === folder && item.type === 'folder');
      if (foundFolder && foundFolder.children) {
        items = foundFolder.children;
      }
    }
    
    setCurrentPath(newPath);
    setCurrentItems(items);
    setSelectedImage(null);
    setPreviewImage(null);
  };

  // Handle image selection
  const handleImageClick = (image: FileItem) => {
    if (image.url) {
      setSelectedImage(image.url);
      setPreviewImage(image.url);
    }
  };

  // Handle image selection confirmation
  const handleSelectImage = () => {
    if (selectedImage) {
      onSelectImage(selectedImage);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('cabinet.mdx_editor.image_uploader.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => handleBreadcrumbClick(-1)}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {t('cabinet.mdx_editor.image_uploader.home')}
            </Link>
            {currentPath.map((folder, index) => (
              <Link
                key={index}
                underline="hover"
                color="inherit"
                sx={{ cursor: 'pointer' }}
                onClick={() => handleBreadcrumbClick(index)}
              >
                {folder}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ height: 400, overflow: 'auto' }}>
              {currentPath.length > 0 && (
                <ListItem disablePadding>
                  <ListItemButton onClick={handleBackClick}>
                    <ListItemIcon>
                      <ArrowBackIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('cabinet.mdx_editor.image_uploader.back')} />
                  </ListItemButton>
                </ListItem>
              )}
              <List>
                {currentItems.map((item) => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton 
                      onClick={() => item.type === 'folder' ? handleFolderClick(item) : handleImageClick(item)}
                      selected={selectedImage === item.url}
                    >
                      <ListItemIcon>
                        {item.type === 'folder' ? <FolderIcon color="primary" /> : <ImageIcon color="secondary" />}
                      </ListItemIcon>
                      <ListItemText primary={item.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2 }}>
              {previewImage ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('cabinet.mdx_editor.image_uploader.preview')}
                  </Typography>
                  <Box 
                    component="img" 
                    src={previewImage} 
                    alt="Preview" 
                    sx={{ 
                      maxWidth: '100%', 
                      maxHeight: '80%', 
                      objectFit: 'contain' 
                    }} 
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('cabinet.mdx_editor.image_uploader.no_selection')}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t('cabinet.mdx_editor.cancel')}
        </Button>
        <Button 
          onClick={handleSelectImage} 
          color="primary" 
          disabled={!selectedImage}
        >
          {t('cabinet.mdx_editor.image_uploader.select')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUploader;